<template>
  <AppDialog
    v-model="isVisible"
    width="min(720px, 92vw)"
    :closable="true"
    :dismissable-mask="true"
    custom-class="backdrop-blur-xl!"
  >
    <template #header>
      <div class="flex min-w-0 flex-1 items-center justify-between text-sm font-semibold text-[var(--p-primary-color)] mr-6">
        <div class="flex min-w-0 items-center">
          <IconPerson width="30" height="30" />
          <Plus :size="16" />
          <span class="truncate">{{ $t('whatsapp.add_contact') }}</span>
        </div>

        <div class="ml-3 flex min-w-0 items-center gap-2">
          <IconCampaign width="25" height="25" />
          <span class="truncate">{{ campaignLabel }}</span>
        </div>
      </div>
    </template>

    <template #content>
      <div class="">
        <div class="space-y-3">
          <div class="flex flex-col gap-4">
            <AppInput
              v-model="currentDraftContact.name"
              :placeholder="$t('general.name')"
              :error-message="currentDraftContact.errors.name"
              class="w-full"
            />

            <div class="flex flex-col gap-4 sm:flex-row">
              <div class="w-full sm:w-1/3">
                <FloatLabel variant="on">
                  <AppSelect
                    v-model="currentDraftContact.country"
                    :options="countryOptions"
                    option-label="name"
                    option-value=""
                    filter
                    size="small"
                    class="w-full !rounded-lg"
                    :error-message="currentDraftContact.errors.country"
                    :show-error-message="false"
                    :filter-placeholder="t('general.search_country')"
                  >
                    <template #value="{ value }">
                      <div v-if="value" class="flex min-w-0 items-center gap-2">
                        <span
                          aria-hidden="true"
                          :class="['country-flag', toLowerCase(value.iso2), 'text-sm']"
                        ></span>
                        <span class="min-w-0 truncate text-sm">{{ value.name }}</span>
                        <span class="shrink-0 text-sm text-[var(--p-surface-500)]">
                          +{{ value.dialCode }}
                        </span>
                      </div>
                    </template>

                    <template #option="{ option }">
                      <div class="flex items-center gap-2">
                        <span :class="['country-flag', toLowerCase(option.iso2), 'text-sm']"></span>
                        <span class="truncate">{{ option.name }}</span>
                        <span class="ml-auto text-[var(--p-surface-500)]">
                          +{{ option.dialCode }}
                        </span>
                      </div>
                    </template>
                  </AppSelect>
                  <label class="text-sm">Código país</label>
                </FloatLabel>

                <div
                  v-if="currentDraftContact.errors.country"
                  class="mt-1 text-red-500 dark:text-red-400"
                >
                  <small>{{ currentDraftContact.errors.country }}</small>
                </div>
              </div>

              <div class="w-full sm:w-2/3">
                <AppInput
                  :model-value="currentDraftContact.phone"
                  :placeholder="$t('calendar.phone_number')"
                  :error-message="currentDraftContact.errors.phone"
                  :show-error-message="false"
                  type="tel"
                  inputmode="numeric"
                  autocomplete="off"
                  :maxlength="currentPhoneMaxDigits"
                  @update:model-value="handlePhoneInput"
                  @blur="validateCurrentPhone"
                />

                <div class="min-h-6 p-0 m-0 text-red-400 dark:text-red-300">
                  <small v-if="currentDraftContact.errors.phone">
                    {{ currentDraftContact.errors.phone }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex w-full gap-10">
        <AppButtonIcon
          label="Agregar otro"
          severity="secondary"
          size="small"
          :disabled="isSaving"
          @click="addDraftContact"
        />

        <AppButtonIcon label="Guardar" size="small" :loading="isSaving" @click="handleSave" />
      </div>
    </template>
  </AppDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import FloatLabel from 'primevue/floatlabel'
import { Plus } from 'lucide-vue-next'
import { validatePhoneNumberLength } from 'libphonenumber-js'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'
import allCountriesData from '@/components/molecules/phone-input/all-countries'
import { usePhoneFormat } from '@/components/molecules/phone-input/composables/usePhoneFormat'
import type { CountryObject } from '@/components/molecules/phone-input/interfaces/countries.interface'
import type { ContactSelection } from '@/components/whatsapp/types/interfaces'
import IconCampaign from '@/assets/svg/campaign_icon.svg?component'
import IconPerson from '@/assets/svg/person_icon.svg?component'
import { useI18n } from 'vue-i18n'
import { useContactService } from '@/services/contact/useContactService'
import { ContactStatus } from '@/services/contact/enums/contact-status.enum'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
interface Props {
  visible: boolean
  campaignName?: string | null
  campaignId?: string | null
}

interface DraftErrors {
  name: string
  country: string
  phone: string
}

interface DraftContact {
  key: string
  name: string
  country: CountryObject | null
  phone: string
  errors: DraftErrors
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  save: [contacts: ContactSelection[]]
}>()
const { t } = useI18n()
const { createContacts } = useContactService()
const { isValidPhoneNumber } = usePhoneFormat()
const preferredCountries = ['CO', 'US', 'MX', 'AR', 'PE', 'CL']
const isSaving = ref(false)

const countryOptions = computed<CountryObject[]>(() => {
  const preferred = allCountriesData.filter((country) => preferredCountries.includes(country.iso2))
  const rest = allCountriesData.filter((country) => !preferredCountries.includes(country.iso2))
  return [...preferred, ...rest]
})

const defaultCountry = computed(
  () =>
    countryOptions.value.find((country) => country.iso2 === 'CO') ??
    countryOptions.value[0] ??
    null,
)

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
})

const campaignLabel = computed(
  () => props.campaignName?.trim() || t('general.no_campaign_selected'),
)

const createDraftContact = (): DraftContact => ({
  key: crypto.randomUUID(),
  name: '',
  country: defaultCountry.value,
  phone: '',
  errors: {
    name: '',
    country: '',
    phone: '',
  },
})

const draftContacts = ref<DraftContact[]>([createDraftContact()])

const currentDraftContact = computed(() => draftContacts.value[draftContacts.value.length - 1]!)

const currentPhoneMaxDigits = computed(() =>
  currentDraftContact.value.country
    ? getMaxNationalDigits(currentDraftContact.value.country)
    : undefined,
)

const toLowerCase = (value: string) => value.toLowerCase()

const resetDraftContacts = () => {
  draftContacts.value = [createDraftContact()]
}

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      resetDraftContacts()
    }
  },
)

const normalizeDigits = (value: string) => value.replace(/\D/g, '')

const getPhoneLengthStatus = (localNumber: string, country: CountryObject) =>
  validatePhoneNumberLength(localNumber, country.iso2)

const getMaxNationalDigits = (country: CountryObject) => {
  const e164MaxNationalDigits = 15 - country.dialCode.length

  for (let length = 1; length <= e164MaxNationalDigits; length += 1) {
    if (getPhoneLengthStatus('9'.repeat(length), country) === 'TOO_LONG') {
      return Math.max(length - 1, 1)
    }
  }

  return e164MaxNationalDigits
}

const isValidCountryPhone = (localNumber: string, country: CountryObject): boolean => {
  if (!/^\+[1-9]\d{1,14}$/.test(`+${country.dialCode}${localNumber}`)) return false

  return isValidPhoneNumber(localNumber, country.iso2)
}

const getInvalidPhoneMessage = (country: CountryObject) =>
  t('general.invalid_phone_for_country', { country: country.name })

const getPhoneValidationMessage = (localNumber: string, country: CountryObject) => {
  const lengthStatus = getPhoneLengthStatus(localNumber, country)

  if (lengthStatus === 'TOO_SHORT') {
    return t('general.phone_number_too_short_for_country', { country: country.name })
  }

  if (lengthStatus === 'TOO_LONG') {
    return t('general.phone_number_too_long_for_country', { country: country.name })
  }

  if (lengthStatus || !isValidCountryPhone(localNumber, country)) {
    return getInvalidPhoneMessage(country)
  }

  return ''
}

const setPhoneValidationError = (contact: DraftContact, required = false) => {
  const localNumber = normalizeDigits(contact.phone)
  contact.phone = localNumber

  if (!localNumber) {
    contact.errors.phone = required ? t('general.required_field') : ''
    return
  }

  if (!contact.country) {
    contact.errors.phone = t('general.invalid_number')
    return
  }

  contact.errors.phone = getPhoneValidationMessage(localNumber, contact.country)
}

const buildNormalizedPhone = (contact: DraftContact) =>
  contact.country ? `+${contact.country.dialCode}${normalizeDigits(contact.phone)}` : ''

const buildPayloadPhone = (contact: DraftContact) =>
  contact.country ? `${contact.country.dialCode}${normalizeDigits(contact.phone)}` : ''

const isDraftContactEmpty = (contact: DraftContact) =>
  !contact.name.trim() && !normalizeDigits(contact.phone)

const validateDraftContact = (contact: DraftContact) => {
  contact.errors = {
    name: '',
    country: '',
    phone: '',
  }

  if (!contact.name.trim()) {
    contact.errors.name = t('general.required_field')
  }

  if (!contact.country) {
    contact.errors.country = t('general.required_field')
  }

  setPhoneValidationError(contact, true)

  return !contact.errors.name && !contact.errors.country && !contact.errors.phone
}

const validateCurrentPhone = () => {
  setPhoneValidationError(currentDraftContact.value)
}

const handlePhoneInput = (value: string | number | null) => {
  const contact = currentDraftContact.value
  const normalizedPhone = normalizeDigits(String(value ?? ''))
  const maxDigits = contact.country ? getMaxNationalDigits(contact.country) : undefined

  contact.phone =
    typeof maxDigits === 'number' ? normalizedPhone.slice(0, maxDigits) : normalizedPhone
  setPhoneValidationError(contact)
}

watch(
  () => currentDraftContact.value.country,
  () => {
    if (normalizeDigits(currentDraftContact.value.phone)) {
      validateCurrentPhone()
    }
  },
)

const addDraftContact = () => {
  const contact = currentDraftContact.value
  const isValid = validateDraftContact(contact)
  const normalizedPhone = buildNormalizedPhone(contact)
  const alreadyExists = draftContacts.value
    .slice(0, -1)
    .some((draftContact) => buildNormalizedPhone(draftContact) === normalizedPhone)

  if (normalizedPhone && alreadyExists) {
    contact.errors.phone = t('general.number_already_exists')
    return
  }

  if (!isValid) return

  draftContacts.value.push(createDraftContact())
}

const handleClose = () => {
  isVisible.value = false
}

const handleSave = async () => {
  const contactsToSave = isDraftContactEmpty(currentDraftContact.value)
    ? draftContacts.value.slice(0, -1)
    : draftContacts.value

  if (!contactsToSave.length) {
    validateDraftContact(currentDraftContact.value)
    return
  }

  const normalizedPhones = contactsToSave.map((contact) => buildNormalizedPhone(contact))

  const phoneOccurrences = normalizedPhones.reduce<Record<string, number>>((acc, phone) => {
    if (!phone) return acc
    acc[phone] = (acc[phone] ?? 0) + 1
    return acc
  }, {})

  const validationResults = contactsToSave.map((contact, index) => {
    const isValid = validateDraftContact(contact)
    const normalizedPhone = normalizedPhones[index]

    if (normalizedPhone && phoneOccurrences[normalizedPhone] > 1) {
      contact.errors.phone = 'Este número ya fue agregado en la lista'
      return false
    }

    return isValid
  })

  const isValid = validationResults.every(Boolean)
  if (!isValid) return

  if (!props.campaignId) return

  const payload = contactsToSave.map((contact) => ({
    name: contact.name.trim(),
    phone: buildPayloadPhone(contact),
    campaign_id: props.campaignId!,
    position: null,
    status: ContactStatus.PENDING,
    custom_fields: null,
  }))

  isSaving.value = true

  try {
    const createdContacts = await createContacts(payload)
    const contacts: ContactSelection[] = createdContacts.map((contact) => ({
      id: contact.id,
      phone: contact.phone,
      name: contact.name,
      campaign_id: contact.campaign_id,
      is_manual: true,
    }))

    emit('save', contacts)
    handleClose()
  } finally {
    isSaving.value = false
  }
}
</script>
